import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../../../lib/supabase";
import type { AssociationActivityRow, EventPopup } from "../../../lib/supabase";

const FIXED_SUPPORT_PROGRAMS = [
  {
    slug: "ket-noi-doanh-nghiep",
    title_vi: "Kết nối doanh nghiệp Hàn - Việt",
    title_kr: "비즈니스 매칭",
    desc_vi: "Tự động ghép cặp doanh nghiệp phù hợp theo ngành hàng và quy mô",
    desc_kr: "업종 및 규모에 맞는 기업 자동 매칭",
    icon: "ri-exchange-2-line",
  },
  {
    slug: "dich-vu-phien-dich",
    title_vi: "Dịch vụ phiên & biên dịch",
    title_kr: "통번역 서비스",
    desc_vi: "Hỗ trợ dịch thuật tài liệu kỹ thuật và hợp đồng Hàn–Việt",
    desc_kr: "기술 문서 및 계약서 번역 지원",
    icon: "ri-translate-2",
  },
  {
    slug: "tu-van-thi-truong",
    title_vi: "Tư vấn xâm nhập thị trường",
    title_kr: "시장 진출 컨설팅",
    desc_vi: "Phân tích thị trường, chiến lược và hỗ trợ lý xuất nhập khẩu",
    desc_kr: "시장 분석, 전략 및 수출입 지원",
    icon: "ri-bar-chart-grouped-line",
  },
  {
    slug: "ho-tro-trien-lam",
    title_vi: "Hỗ trợ tham gia triển lãm",
    title_kr: "전시회 참가 지원",
    desc_vi: "Kết nối gian hàng tại các hội chợ thương mại Hàn Quốc và Việt Nam",
    desc_kr: "한국 및 베트남 무역 박람회 부스 연결",
    icon: "ri-store-2-line",
  },
  {
    slug: "tim-kiem-doi-tac",
    title_vi: "Tìm kiếm đối tác chiến lược",
    title_kr: "파트너 발굴",
    desc_vi: "Tìm kiếm hồ sơ và giới thiệu đối tác phù hợp theo nhu cầu",
    desc_kr: "필요에 맞는 파트너 프로필 검색 및 소개",
    icon: "ri-search-eye-line",
  },
];

type CombinedItem =
  | { type: "activity"; data: AssociationActivityRow }
  | { type: "event"; data: EventPopup };

const AssociationEventsSection = () => {
  const { i18n } = useTranslation();
  const isKr = i18n.language === "kr";

  const [activities, setActivities] = useState<AssociationActivityRow[]>([]);
  const [events, setEvents] = useState<EventPopup[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      const [actRes, evtRes] = await Promise.all([
        supabase
          .from("association_activities")
          .select("id, title, title_kr, subtitle, image_url, category, published_at, is_featured")
          .eq("is_published", true)
          .order("published_at", { ascending: false })
          .limit(8),
        supabase
          .from("event_popups")
          .select("*")
          .order("is_active", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(8),
      ]);
      setActivities((actRes.data as AssociationActivityRow[]) ?? []);
      setEvents((evtRes.data as EventPopup[]) ?? []);
      setLoading(false);
    };

    fetchAll();

    const actChannel = supabase
      .channel("home_assoc_v5")
      .on("postgres_changes", { event: "*", schema: "public", table: "association_activities" }, fetchAll)
      .subscribe();

    const evtChannel = supabase
      .channel("home_evt_v5")
      .on("postgres_changes", { event: "*", schema: "public", table: "event_popups" }, fetchAll)
      .subscribe();

    return () => {
      supabase.removeChannel(actChannel);
      supabase.removeChannel(evtChannel);
    };
  }, []);

  const combined: CombinedItem[] = [
    ...activities.map((a) => ({ type: "activity" as const, data: a })),
    ...events.map((e) => ({ type: "event" as const, data: e })),
  ].sort((a, b) => {
    const dateA =
      a.type === "activity"
        ? (a.data.published_at ?? "")
        : ((a.data as EventPopup).created_at ?? "");
    const dateB =
      b.type === "activity"
        ? (b.data.published_at ?? "")
        : ((b.data as EventPopup).created_at ?? "");
    return dateB.localeCompare(dateA);
  });

  const leftItems = combined.slice(0, 2);
  const rightItems = combined.slice(2, 8);

  return (
    <section className="py-8 bg-[#EDF2FB]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        {/* Section header — desktop only shows both labels side by side */}
        <div className="hidden md:flex items-center justify-between mb-5">
          <span className="text-[11px] font-bold text-white bg-[#004A99] px-3 py-1 rounded-full uppercase tracking-widest">
            {isKr ? "협회 활동 & 이벤트" : "HOẠT ĐỘNG HIỆP HỘI & SỰ KIỆN"}
          </span>
          <span className="text-[11px] font-bold text-white bg-[#004A99] px-3 py-1 rounded-full uppercase tracking-widest">
            {isKr ? "협력 지원" : "HỖ TRỢ HỢP TÁC"}
          </span>
        </div>
        {/* Mobile: only show activity label */}
        <div className="flex md:hidden items-center mb-4">
          <span className="text-[11px] font-bold text-white bg-[#004A99] px-3 py-1 rounded-full uppercase tracking-widest">
            {isKr ? "협회 활동 & 이벤트" : "HOẠT ĐỘNG HIỆP HỘI & SỰ KIỆN"}
          </span>
        </div>

        {/* 2-column layout: Left 70% | Right 30% */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5 items-stretch">

          {/* ── LEFT: Hoạt động HH & Sự kiện (70%) ── */}
          <div className="bg-white rounded-2xl overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 pt-4 pb-3">
              <h2 className="text-sm font-black text-[#212529]">
                {isKr ? "협회 활동 & 이벤트" : "Hoạt Động Hiệp Hội & Sự Kiện"}
              </h2>
              <Link
                to="/hoat-dong-hiep-hoi-va-su-kien"
                className="flex items-center gap-1 text-xs text-[#004A99] font-semibold hover:underline cursor-pointer whitespace-nowrap"
              >
                {isKr ? "전체 보기" : "Xem tất cả"}
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            {loading ? (
              <div className="flex-1 bg-gray-100 animate-pulse mx-5 mb-5 rounded-xl" style={{ minHeight: 240 }} />
            ) : combined.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center py-10 bg-gray-50 mx-5 mb-5 rounded-xl">
                <i className="ri-newspaper-line text-4xl text-gray-200 block mb-2" />
                <p className="text-gray-400 text-sm">{isKr ? "아직 게시물이 없습니다" : "Chưa có bài viết nào"}</p>
              </div>
            ) : (
              <div className="flex flex-col flex-1 px-5 pb-5">
                {/* Layout: cột trái 2 bài xếp dọc + cột phải 5 bài xếp dọc */}
                {/* Mobile: xếp dọc toàn bộ; Desktop: 2 cột ngang */}
                <div className="flex flex-col md:flex-row gap-4 flex-1">
                  {/* Cột trái — 2 bài to xếp dọc */}
                  <div className="flex-[2] min-w-0 flex flex-col gap-4">
                    {leftItems.map((item, idx) => (
                      <div key={idx} className={`flex-1 min-w-0 ${idx > 0 ? "border-t border-gray-100 pt-4" : ""}`}>
                        <NewestItem item={item} isKr={isKr} isSecond={idx === 1} />
                      </div>
                    ))}
                  </div>

                  {/* Cột phải — tối đa 5 bài xếp dọc */}
                  {rightItems.length > 0 && (
                    <div className="flex-[1] flex flex-col gap-3 md:border-l border-t md:border-t-0 border-gray-100 md:pl-4 pt-4 md:pt-0" style={{ minWidth: 0 }}>
                      {rightItems.map((item, idx) => (
                        <OlderItem key={idx} item={item} isKr={isKr} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT: Hỗ trợ hợp tác (30%) ── */}
          <div
            className="rounded-2xl p-5 flex flex-col"
            style={{ background: "linear-gradient(135deg, #004A99 0%, #003d7d 60%, #00AEEF 100%)" }}
          >
            {/* Mobile label for this section */}
            <div className="flex md:hidden items-center mb-3">
              <span className="text-[11px] font-bold text-white bg-white/20 px-3 py-1 rounded-full uppercase tracking-widest">
                {isKr ? "협력 지원" : "HỖ TRỢ HỢP TÁC"}
              </span>
            </div>
            {/* Header */}
            <div className="mb-1">
              <h2 className="text-base font-black text-white leading-tight">
                {isKr ? "한-베 협력 지원 프로그램" : "Chương trình Hỗ trợ Hợp tác Hàn - Việt"}
              </h2>
              <p className="text-white/70 text-xs mt-1 leading-relaxed">
                {isKr
                  ? "양국 기업이 빠르고 효율적으로 연결될 수 있도록 지원합니다"
                  : "Giúp doanh nghiệp hai nước kết nối nhanh chóng và hiệu quả"}
              </p>
            </div>

            <div className="flex-1 flex flex-col gap-2 mt-3">
              {FIXED_SUPPORT_PROGRAMS.map((prog) => (
                <Link
                  key={prog.slug}
                  to={`/support/${prog.slug}`}
                  className="group flex items-start gap-2.5 p-3 rounded-xl bg-white/10 hover:bg-white/20 transition-all cursor-pointer border border-white/10 hover:border-white/30 flex-1"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 flex-shrink-0 mt-0.5">
                    <i className={`${prog.icon} text-white text-sm`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-white leading-tight">
                      {isKr ? prog.title_kr : prog.title_vi}
                    </p>
                    <p className="text-[10px] text-white/60 mt-0.5 leading-snug line-clamp-2">
                      {isKr ? prog.desc_kr : prog.desc_vi}
                    </p>
                  </div>
                  <i className="ri-arrow-right-s-line text-white/40 group-hover:text-white transition-colors flex-shrink-0 text-sm mt-0.5" />
                </Link>
              ))}
            </div>

            <p className="text-[10px] text-white/40 mt-3 text-center">
              {isKr ? "한-베 전문 지원 — 회원 가입 시 무료" : "Hỗ trợ toàn diện"}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Bài mới nhất (to, bên trái, chiếm ~65%) ──
const NewestItem = ({ item, isKr, isSecond = false }: { item: CombinedItem; isKr: boolean; isSecond?: boolean }) => {
  const isActivity = item.type === "activity";
  const activity = isActivity ? (item.data as AssociationActivityRow) : null;
  const event = !isActivity ? (item.data as EventPopup) : null;

  const title = isActivity
    ? (isKr && activity?.title_kr ? activity.title_kr : activity?.title ?? "")
    : (isKr && event?.title_kr ? event.title_kr : event?.title_vi ?? "");
  const imageUrl = isActivity ? activity?.image_url : event?.image_url;
  const href = isActivity
    ? `/hoat-dong-hiep-hoi-va-su-kien/${activity?.id}`
    : `/su-kien/${event?.id}`;
  const date = isActivity
    ? (activity?.published_at ? new Date(activity.published_at).toLocaleDateString("vi-VN") : "")
    : (event?.event_date ?? "");
  const bgColor = event?.bg_color ?? "#004A99";

  return (
    <Link to={href} className="group flex flex-col cursor-pointer h-full">
      {/* Ảnh to */}
      <div className="relative w-full rounded-xl overflow-hidden" style={{ height: isSecond ? 160 : 220 }}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: isActivity ? "#EDF2FB" : `${bgColor}20` }}
          >
            <i
              className={`${isActivity ? "ri-newspaper-line text-[#004A99]/30" : "ri-calendar-event-line"} text-5xl`}
              style={{ color: isActivity ? undefined : bgColor }}
            />
          </div>
        )}
        {/* Tag MỚI — chỉ hiện ở bài đầu tiên */}
        {!isSecond && (
          <span className="absolute top-3 left-3 bg-red-500 text-white text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wide">
            MỚI
          </span>
        )}
        {/* Tag loại */}
        <span
          className={`absolute top-3 right-3 text-[10px] font-bold px-2.5 py-1 rounded-full ${
            isActivity ? "bg-[#004A99] text-white" : "bg-green-500 text-white"
          }`}
        >
          {isActivity ? (isKr ? "협회 활동" : "Hiệp Hội") : (isKr ? "이벤트" : "Sự Kiện")}
        </span>
        {/* Active badge for events */}
        {!isActivity && event?.is_active && (
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
            {isKr ? "진행 중" : "Đang diễn ra"}
          </span>
        )}
      </div>
      {/* Tiêu đề to */}
      <div className="pt-3 flex-1">
        <h3 className="text-base font-black text-[#212529] leading-snug group-hover:text-[#004A99] transition-colors mb-1 line-clamp-3">
          {title}
        </h3>
        {date && (
          <p className="text-[11px] text-gray-400 flex items-center gap-1">
            <i className="ri-calendar-line" />
            {date}
          </p>
        )}
      </div>
    </Link>
  );
};

// ── Bài cũ hơn (nhỏ, xếp dọc bên phải) ──
const OlderItem = ({ item, isKr }: { item: CombinedItem; isKr: boolean }) => {
  const isActivity = item.type === "activity";
  const activity = isActivity ? (item.data as AssociationActivityRow) : null;
  const event = !isActivity ? (item.data as EventPopup) : null;

  const title = isActivity
    ? (isKr && activity?.title_kr ? activity.title_kr : activity?.title ?? "")
    : (isKr && event?.title_kr ? event.title_kr : event?.title_vi ?? "");
  const imageUrl = isActivity ? activity?.image_url : event?.image_url;
  const href = isActivity
    ? `/hoat-dong-hiep-hoi-va-su-kien/${activity?.id}`
    : `/su-kien/${event?.id}`;
  const bgColor = event?.bg_color ?? "#004A99";

  return (
    <Link
      to={href}
      className="group flex flex-col cursor-pointer"
    >
      {/* Mobile: ảnh trên, chữ dưới. Desktop: ảnh trái, chữ phải */}
      <div className="flex flex-col sm:flex-row items-start gap-3">
        {/* Hình nhỏ — fixed size */}
        <div className="w-full sm:w-[60px] h-[140px] sm:h-[60px] rounded-lg overflow-hidden flex-shrink-0">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ backgroundColor: isActivity ? "#EDF2FB" : `${bgColor}20` }}
            >
              <i
                className={`${isActivity ? "ri-newspaper-line text-[#004A99]/40" : "ri-calendar-event-line"} text-sm`}
                style={{ color: isActivity ? undefined : bgColor }}
              />
            </div>
          )}
        </div>
        {/* Tiêu đề — không giới hạn width, hiển thị đủ 2 dòng */}
        <div className="flex-1 min-w-0">
          <span
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full mb-1 inline-block ${
              isActivity ? "bg-[#004A99]/10 text-[#004A99]" : "bg-green-100 text-green-700"
            }`}
          >
            {isActivity ? (isKr ? "협회" : "HH") : (isKr ? "이벤트" : "SK")}
          </span>
          <p
            className="font-semibold text-[#212529] group-hover:text-[#004A99] transition-colors leading-snug"
            style={{
              fontSize: "14px",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {title}
          </p>
        </div>
      </div>
    </Link>
  );
};

export default AssociationEventsSection;
